"use client";

import blocks from "@/components/page/blocks.module.css";
import type { CorpusGroup } from "@/content/nano";
import { decimal, thousands } from "@/content/research-data";
import styles from "./charts.module.css";
import { useChartFrame } from "./useChartFrame";

export type CorpusChartProps = {
  groups: CorpusGroup[];
  title: string;
  /** Pie del gráfico (letra chica). */
  caption?: string;
  hideTitle?: boolean;
  id?: string;
  className?: string;
};

/**
 * Composición del corpus en barras horizontales de HTML: cada fila es un grupo, con su
 * porcentaje sobre el total de documentos. La barra mide la proporción real (sobre
 * 100 %), por eso la web domina: así fue el corpus. La tabla completa, fuente por
 * fuente, queda en el desplegable.
 */
export function CorpusChart({ groups, title, caption, hideTitle = false, id = "grafico-corpus", className }: CorpusChartProps) {
  const { ref, inView } = useChartFrame<HTMLOListElement>(0);

  const sum = (g: CorpusGroup) => g.sources.reduce((acc, s) => acc + s.documents, 0);
  const total = groups.reduce((acc, g) => acc + sum(g), 0);
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  const titleId = `${id}-titulo`;

  return (
    <figure
      id={id}
      className={[styles.chart, className].filter(Boolean).join(" ")}
      data-inview={inView ? "true" : undefined}
      aria-labelledby={titleId}
    >
      <div className={styles.header}>
        <p id={titleId} className={hideTitle ? "sr-only" : styles.title}>
          {title}
        </p>
        <p className={styles.subtitle}>{thousands(total)} documentos · porcentaje sobre el total</p>
      </div>

      <ol ref={ref} className={styles.rows}>
        {groups.map((g, i) => {
          const n = sum(g);
          const p = pct(n);
          return (
            <li key={g.id} className={styles.row}>
              <div className={styles.rowHead}>
                <span className={styles.rowLabel}>{g.label}</span>
                <span className={styles.rowValue}>
                  <strong>{decimal(p, 2)}&nbsp;%</strong> · {thousands(n)} documentos
                </span>
              </div>
              <div className={styles.track} aria-hidden="true">
                <span className={`${styles.fill} ${styles.grow}`} data-order={Math.min(i, 3)} style={{ width: `${p}%` }} />
              </div>
              <span className={styles.rowSources}>{g.sources.map((s) => s.id).join(" · ")}</span>
            </li>
          );
        })}
      </ol>

      {caption && (
        <figcaption className={styles.fine}>
          <span>{caption}</span>
          <span>Fuente: tabla «Composición del corpus» del model card de YvyrAI-100m-Nano.</span>
        </figcaption>
      )}

      <details className={styles.data}>
        <summary>Ver la tabla completa</summary>
        <div className={`${styles.dataBody} ${blocks.tableWrap}`}>
          <table className={blocks.table}>
            <caption>Documentos por fuente y grupo</caption>
            <thead>
              <tr>
                <th scope="col">Fuente</th>
                <th scope="col">Grupo</th>
                <th scope="col" className={blocks.num}>
                  Documentos
                </th>
                <th scope="col" className={blocks.num}>
                  %
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.flatMap((g) =>
                g.sources.map((s) => (
                  <tr key={s.id}>
                    <th scope="row" className={blocks.mono}>
                      {s.id}
                    </th>
                    <td>{g.label}</td>
                    <td className={blocks.num}>{thousands(s.documents)}</td>
                    <td className={blocks.num}>{decimal(pct(s.documents), 2)}</td>
                  </tr>
                )),
              )}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                <td />
                <td className={blocks.num}>{thousands(total)}</td>
                <td className={blocks.num}>100,00</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </details>
    </figure>
  );
}
